import React, {
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { BaseTextFieldProps } from '@mui/material/TextField/TextField';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';

import clsx from 'clsx';

import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrowDownIcon.svg';
import { useRegisterDismissLayer } from '../../../../context/BackLayerStackContext';
import { NewItemId } from '../../../../shared/constants';
import {
  getSelectOptionLabel,
  isSelectOptionEmpty,
} from '../../../../utils/forms/select-placeholder-utils';
import CloseActionButton from '../../ActionButtons/CloseActionButton';
import ErrorTooltipIcon from '../ErrorTooltipIcon';
import {
  MENU_VIEWPORT_MAX_HEIGHT,
  getMenuProps,
  useStyles,
} from './AppSelectWrapper.styles';

const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

/** MUI Select/Menu: mousedown on items can swallow click; preventDefault keeps click firing. */
function SelectCloseActionButton({
  onClose,
}: {
  onClose: (e: SyntheticEvent) => void;
}) {
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex' }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}>
      <CloseActionButton onClick={onClose} />
    </Box>
  );
}

export interface IAppSelectWrapper extends BaseTextFieldProps {
  additionalOptions?: Array<any>;
  defaultValue?: any;
  options?: Array<any>;
  explainingText?: string;
  disableSorting?: boolean;
  disableClear?: boolean;
  sortBy?: string;
  autoWidth?: boolean;
  label?: React.ReactNode;
  name?: string;
  onChange?: (value?: any) => void;
  onClearCallback?: () => void;
  error?: boolean;
  helperText?: any;
  viewMode?: boolean;
  fieldName?: string;
  disableSearch?: boolean;
  /** Custom render for each option in the dropdown (like MUI Autocomplete renderOption) */
  renderOption?: (item: any) => React.ReactNode;
  /** Custom render for the selected value in the input (like MUI Select renderValue) */
  renderValue?: (value: any | null) => React.ReactNode;
  /** Placeholder text when no option is selected */
  placeholder?: string;
  mode?: 'light' | 'dark';
}

const AppSelectWrapper: React.FC<IAppSelectWrapper> = ({
  disabled = false,
  name,
  className = '',
  additionalOptions = [],
  options = [],
  label,
  defaultValue,
  explainingText = undefined,
  disableSorting = false,
  disableClear = false,
  fieldName = 'name',
  sortBy = fieldName,
  autoWidth = true,
  error = false,
  renderOption,
  renderValue,
  placeholder,
  onChange = (value?: any) => {},
  onClearCallback = () => {},
  helperText,
  viewMode = false,
  disableSearch = false,
  mode = 'light',
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { classes } = useStyles({ mode });
  const hasLabel = Boolean(label);
  const labelClassName = clsx(
    classes.label,
    explainingText ? classes.labelWithExplaining : null,
  );
  const inputLabelProps = hasLabel
    ? {
        ...(props.InputLabelProps || {}),
        shrink: true,
        className: clsx(props.InputLabelProps?.className, labelClassName),
      }
    : props.InputLabelProps;
  const [searchText, setSearchText] = useState('');
  const [selectMenuOpen, setSelectMenuOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const closeSelectMenu = useCallback(() => {
    setSelectMenuOpen(false);
    setTimeout(() => (document.activeElement as HTMLElement)?.blur());
  }, []);
  useRegisterDismissLayer(selectMenuOpen, closeSelectMenu);

  const index = useMemo(
    () =>
      [...additionalOptions, ...options]?.findIndex((option) => {
        const optionId = option?.id;

        return optionId === defaultValue || optionId === defaultValue?.id;
      }),
    [options, additionalOptions, defaultValue],
  );

  const sortedOptions = useMemo(() => {
    const mainOptions = !disableSorting
      ? (options ? [...options] : [])?.sort((a, b) =>
          `${a[sortBy]}`.localeCompare(`${b[sortBy]}`),
        )
      : options;
    return [...additionalOptions, ...mainOptions];
  }, [options, additionalOptions, disableSorting, sortBy]);

  const valueOutOfOption = useMemo(() => {
    const hasDefaultValue = index === -1 && defaultValue?.[fieldName];

    if (hasDefaultValue) {
      return {
        [fieldName]: defaultValue[fieldName],
        disabled: true,
      };
    }

    return null;
  }, [index, defaultValue]);

  const value = useMemo(
    () =>
      index === -1 || index === undefined
        ? valueOutOfOption || ''
        : [...additionalOptions, ...options][index],
    [index, valueOutOfOption, options, additionalOptions],
  );

  const resolvedRenderValue = useMemo(() => {
    if (renderValue) {
      return renderValue;
    }

    if (!placeholder) {
      return undefined;
    }

    return (selected: unknown) => {
      if (isSelectOptionEmpty(selected, fieldName)) {
        return (
          <Typography variant="text-md" color="text.secondary">
            {placeholder}
          </Typography>
        );
      }

      return getSelectOptionLabel(selected, fieldName);
    };
  }, [renderValue, placeholder, fieldName]);

  const renderOptions = useCallback(() => {
    const renderItems = valueOutOfOption
      ? [valueOutOfOption, ...(sortedOptions || [])]
      : sortedOptions;

    return renderItems?.map((item, index) => (
      <MenuItem
        className={clsx({
          [classes.hidden]:
            item?.id !== NewItemId &&
            (!containsText(item?.[fieldName], searchText) || item.disabled),
        })}
        key={`${item.id}${item[fieldName]}${index}`}
        value={item}
        id={item.id}>
        {renderOption ? renderOption(item) : item[fieldName]}
      </MenuItem>
    ));
  }, [
    valueOutOfOption,
    sortedOptions,
    searchText,
    fieldName,
    renderOption,
    classes.hidden,
  ]);

  if (!(options.length + additionalOptions.length)) {
    return (
      <FormControl className={classes.formControl}>
        <TextField
          fullWidth
          disabled={true}
          label={label}
          InputLabelProps={inputLabelProps}
          InputProps={hasLabel ? { notched: false } : undefined}
          className={clsx(className, classes.root)}
          value={defaultValue?.[fieldName]}
          name={name}
          variant="outlined"
          {...props}
          margin="none"
        />
      </FormControl>
    );
  }

  function renderEndAdornment() {
    const closeIcon = !disabled && defaultValue && !disableClear && (
      <IconButton
        disabled={disabled}
        className={classes.closeIcon}
        aria-label="remove value"
        onClick={() => {
          onChange(null);
        }}
        size="large">
        <CloseIcon />
      </IconButton>
    );

    const errorIcon = error && <ErrorTooltipIcon helperText={helperText} />;

    return (
      <InputAdornment
        position="end"
        style={{ right: 35, position: 'absolute' }}>
        {closeIcon}
        {errorIcon}
      </InputAdornment>
    );
  }

  if (viewMode)
    return (
      <Box className={classes.viewModeWrapper}>
        {label && <Typography variant="text-md-sb">{label}</Typography>}
        <Typography variant="text-md">{defaultValue?.name}</Typography>
      </Box>
    );

  const menuProps = getMenuProps(theme);

  return (
    <FormControl className={classes.formControl}>
      <TextField
        fullWidth
        select
        label={label}
        disabled={disabled}
        id={name}
        name={name}
        ref={ref}
        variant="outlined"
        value={value}
        onChange={(d: React.ChangeEvent<HTMLInputElement>) => {
          onChange(d);
        }}
        error={error}
        // helperText={helperText}
        className={clsx(className, classes.root)}
        SelectProps={{
          autoWidth,
          open: selectMenuOpen,
          onOpen: () => setSelectMenuOpen(true),
          onClose: closeSelectMenu,
          IconComponent: ArrowDownIcon,
          ...(resolvedRenderValue && {
            displayEmpty: true,
            renderValue: () => resolvedRenderValue(value ?? null),
          }),
          MenuProps: {
            ...menuProps,
            PaperProps: {
              ...menuProps.PaperProps,
              sx: {
                ...(menuProps.PaperProps?.sx as Record<string, unknown>),
                [theme.breakpoints.up('md')]: {
                  width: ref?.current?.offsetWidth,
                  maxWidth: `${ref?.current?.offsetWidth} !important`,
                  maxHeight: MENU_VIEWPORT_MAX_HEIGHT,
                },
              },
            },
          },
        }}
        InputLabelProps={inputLabelProps}
        InputProps={{
          ...(hasLabel ? { notched: false } : {}),
          endAdornment: renderEndAdornment(),
          startAdornment: explainingText ? (
            <InputAdornment position="start">
              <Tooltip
                title={explainingText}
                classes={{ popper: classes.toolTip }}>
                <IconButton size="large" className={classes.explainingToolTip}>
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
        {...props}>
        {disableSearch ? (
          <ListSubheader
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'flex-end',
              alignItems: 'center',
              lineHeight: 1,
              p: theme.spacing(1),
            }}>
            <SelectCloseActionButton
              onClose={(e) => {
                e.stopPropagation();
                e.preventDefault();
                closeSelectMenu();
              }}
            />
          </ListSubheader>
        ) : (
          <>
            <ListSubheader
              sx={{
                display: { xs: 'block', md: 'none' },
                lineHeight: 1,
                p: theme.spacing(1),
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}>
                <TextField
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  className={classes.searchField}
                  autoFocus={isMobile}
                  variant="outlined"
                  placeholder={t('common.typeToSearch')}
                  value={searchText}
                  sx={{ flex: 1, minWidth: 0 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onFocus={() => {
                    setSearchText('');
                  }}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                />
                <SelectCloseActionButton
                  onClose={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    closeSelectMenu();
                  }}
                />
              </Box>
            </ListSubheader>
            <ListSubheader
              sx={{
                display: { xs: 'none', md: 'block' },
                p: theme.spacing(1),
              }}>
              <TextField
                onClick={(e) => e.stopPropagation()}
                size="small"
                className={classes.searchField}
                autoFocus={!isMobile}
                variant="outlined"
                placeholder={t('common.typeToSearch')}
                fullWidth
                value={searchText}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onFocus={() => {
                  setSearchText('');
                }}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    e.stopPropagation();
                  }
                }}
              />
            </ListSubheader>
          </>
        )}
        {renderOptions()}
      </TextField>
    </FormControl>
  );
};

export default AppSelectWrapper;
